import './styles.scss';
import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { loadSelectionCode } from '../../store/CodePanel/actions';
import { selectDarkToggle } from '../../store/NavigationBar/selectors';
import { setExample, setIsOpen as setIsOpenAction } from '../../store/ExampleGallery/actions';
import { exampleTemplates } from '../../utils/templateGenerator';
import { sendGAEvent } from '../../store/GoogleAnalytics/actions';
import { USE_DEMO_EXAMPLES, CLICK_LINK_EXAMPLE_BUTTON } from '../../constants/google-analytics';
import { inactiveBtn, activeBtn } from '../../constants/example-gallery-popup';
import { selectEmbeddingMainType } from '../../store/OperationsMenu/selectors';
import { selectExampleId, selectIsOpen } from '../../store/ExampleGallery/selectors';
import { changeEmbeddingSubType } from '../../store/OperationsMenu/actions';

const ExampleGallery = ({
  isDarkTheme,
  setIsOpen,
  onLoadSelectionCode,
  onSendGAEvent,
  onSetExample,
  onChangeEmbeddingSubType,
  embeddingType,
  isOpen,
  exampleId,
}) => {
  // Handles states for local tab selection. Can choose exampleTab or yourDossierTab.
  // Handles states for example selection
  const [selectedExample, setSelectedExample] = useState(
    Object.values(exampleTemplates.dashboard.content.general.items)[0],
  );
  const [selectedGroup, setSelectedGroup] = useState(exampleTemplates.dashboard.content.general);

  // Handles states for custom dashboard selection

  // Handles states for link button
  const shareLinkText = 'Copy Link';
  const linkCopiedText = 'Link Copied';
  const [linkBtnText, setLinkBtnText] = useState(shareLinkText);
  const [exampleTemplatesOfType, setExampleTemplatesOfType] = useState(
    exampleTemplates.dashboard.content,
  );

  /* eslint-disable no-restricted-syntax */
  const setExampleFromId = (id, content) => {
    for (const exampleGroup of Object.values(content)) {
      for (const example of Object.values(exampleGroup.items)) {
        if (example.id === id) {
          setSelectedGroup(exampleGroup);
          setSelectedExample({
            groupName: exampleGroup.groupName,
            dossierUrl: example.defaultUrl,
            id: example.id,
            desc: example.desc,
            name: example.name,
            embeddingType: example.embeddingType,
            image: example.image,
            subType: example.subType,
          });
        }
      }
    }
  };
  /* eslint-enable no-restricted-syntax */

  useEffect(() => {
    const exampleTypeContent = Object.values(exampleTemplates).find(
      (example) => example.embeddingType === embeddingType,
    );
    setExampleTemplatesOfType(exampleTypeContent.content);
    if (exampleId) {
      setExampleFromId(exampleId, exampleTypeContent.content);
    } else {
      const defaultGroup = Object.values(exampleTypeContent.content)[0];
      setSelectedGroup(defaultGroup);
      const defaultExample = Object.values(defaultGroup.items)[0];
      setSelectedExample({
        groupName: defaultGroup.groupName,
        dossierUrl: defaultExample.defaultUrl,
        id: defaultExample.id,
        desc: defaultExample.desc,
        name: defaultExample.name,
        embeddingType: defaultExample.embeddingType,
        image: defaultExample.image,
        subType: defaultExample.subType,
      });
    }
  }, [embeddingType, exampleId]);

  // The onclick handler of "Link" button. Receives the example id and name. Generates the link url and copies it to clipboard.
  const onLinkUrl = (id, name) => {
    if (linkBtnText === shareLinkText) {
      const currentPath = window.location.href;
      const result = `${currentPath}?example=${id}`;
      onSendGAEvent(CLICK_LINK_EXAMPLE_BUTTON, name);
      navigator.clipboard.writeText(result);

      // Change the linkBtnText to copiedToClipboardText
      setLinkBtnText(linkCopiedText);
      // After 2 second change the linkBtnText back to shareLinkText
      setTimeout(() => {
        setLinkBtnText(shareLinkText);
      }, 2000);
    }
  };

  // Returns element for the example groups
  const exampleGroupsListElement = (exampleGroupsList) => {
    return (
      <div className="example-groups">
        {exampleGroupsList.map((exampleGroup) => {
          if (exampleGroup.groupName === selectedGroup.groupName) {
            return (
              <div key={`${exampleGroup.groupName}--selected`} className="example-group--selected">
                <div className="text">{exampleGroup.groupName}</div>
                <div className="number">{Object.values(exampleGroup.items).length}</div>
              </div>
            );
          }
          return (
            <div
              key={exampleGroup.groupName}
              className="example-group"
              onClick={() => {
                setSelectedGroup(exampleGroup);
                const example = Object.values(exampleGroup.items)[0];
                setSelectedExample({
                  groupName: exampleGroup.groupName,
                  dossierUrl: example.defaultUrl,
                  id: example.id,
                  desc: example.desc,
                  name: example.name,
                  embeddingType: example.embeddingType,
                  image: example.image,
                  subType: example.subType,
                });
              }}
            >
              <div className="text">{exampleGroup.groupName}</div>
              <div className="number">{Object.values(exampleGroup.items).length}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Returns a single example element
  const exampleElement = (example, groupName) => {
    return (
      <div
        key={example.id}
        className={
          selectedExample.id === example.id
            ? 'example-container example-container--selected'
            : 'example-container'
        }
        onClick={() =>
          setSelectedExample({
            groupName,
            dossierUrl: example.defaultUrl,
            id: example.id,
            desc: example.desc,
            name: example.name,
            embeddingType: example.embeddingType,
            image: example.image,
            subType: example.subType,
          })
        }
      >
        <img className="example-container__img" alt={example.name} src={example.image} />
        <div className="example-container__name">{example.name}</div>
      </div>
    );
  };

  // Returns selected example or group desc element
  const selectedDescElement = (example, group) => {
    if (example && example.name && example.desc) {
      return (
        <div key={`top-desc__${example.id}`} className="top-desc">
          <div className="top-desc__title">{example.name}</div>
          <div className="top-desc__desc">{example.desc}</div>
        </div>
      );
    }
    return (
      <div key={`top-desc__${group.groupName}`} className="top-desc">
        <div className="top-desc__title">{group.groupName}</div>
        <div className="top-desc__desc">{group.groupDesc}</div>
      </div>
    );
  };

  const processEmbeddingSubType = (subType) => {
    if (subType) {
      onChangeEmbeddingSubType(subType);
    }
  };

  // Returns example-tab body element
  const exampleTabBodyElement = () => {
    const selectedGroupItemsArray = Object.values(selectedGroup.items ? selectedGroup.items : {});
    const isExampleSelected = selectedExample && selectedExample.id && selectedExample.name;
    const curBtn = isExampleSelected ? activeBtn : inactiveBtn;
    return (
      <div className="example-gallery-container__content__body">
        <div className="example-gallery-container__content__body__top-desc">
          {selectedDescElement(selectedExample, selectedGroup)}
        </div>
        <div className="example-gallery-container__content__body__examples-container">
          {selectedGroupItemsArray.map((exampleObject) => {
            return exampleElement(exampleObject, selectedGroup.groupName);
          })}
        </div>
        <div className="example-gallery-container__content__body__buttons-bar">
          <button
            className={`${activeBtn} link-button${isExampleSelected ? '' : '--disabled'}`}
            onClick={() => {
              onLinkUrl(selectedExample.id, selectedExample.name);
            }}
            type="button"
          >
            {linkBtnText}
          </button>
          <button
            className={curBtn}
            onClick={() => {
              setIsOpen(false);
              processEmbeddingSubType(selectedExample.subType);
              onLoadSelectionCode(embeddingType, selectedExample.name, selectedExample.groupName);
              onSendGAEvent(USE_DEMO_EXAMPLES, selectedExample.name);
              onSetExample({
                dossierUrl: selectedExample.dossierUrl,
                exampleGroupName: selectedExample.groupName,
                exampleName: selectedExample.name,
                exampleId: selectedExample.id,
                exampleDesc: selectedExample.desc,
                image: selectedExample.image,
              });
            }}
            type="button"
          >
            Start
          </button>
        </div>
      </div>
    );
  };
  // Generates the content for each tab
  // todo: add example here
  const exampleGalleryContent = () => {
    const contentBody = exampleTabBodyElement();
    return (
      <div className="example-gallery-container__content">
        <div className="example-gallery-container__content__left-bar">
          {exampleGroupsListElement(Object.values(exampleTemplatesOfType))}
        </div>
        {contentBody}
      </div>
    );
  };

  const exampleGalleryPopup = () => (
    <div id="example-gallery-container">
      <div className="example-gallery-container__title">
        <div className="example-gallery-container__title__content">
          {embeddingType} Embedding Template
        </div>
        <div
          className="example-gallery-container__title__closeButton"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          <span className="material-icons">close</span>
        </div>
      </div>
      {exampleGalleryContent()}
    </div>
  );

  return (
    <Modal
      appElement={document.getElementById('root')}
      className={isDarkTheme ? 'example-gallery dark' : 'example-gallery'}
      overlayClassName="example-gallery-overlay"
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
    >
      {exampleGalleryPopup()}
    </Modal>
  );
};

ExampleGallery.propTypes = {
  isDarkTheme: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onLoadSelectionCode: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
  embeddingType: PropTypes.string.isRequired,
  onSetExample: PropTypes.func.isRequired,
  exampleId: PropTypes.string.isRequired,
  onChangeEmbeddingSubType: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    isDarkTheme: selectDarkToggle(state),
    embeddingType: selectEmbeddingMainType(state),
    isOpen: selectIsOpen(state),
    exampleId: selectExampleId(state),
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onLoadSelectionCode: loadSelectionCode,
      setIsOpen: setIsOpenAction,
      onSendGAEvent: sendGAEvent,
      onSetExample: setExample,
      onChangeEmbeddingSubType: changeEmbeddingSubType,
    },
    dispatch,
  );
}
export default connect(mapStateToProps, mapDispatchToProps)(ExampleGallery);
